import type { CaseRecord, RecordFact } from "../types";
import { parseAmount } from "./factMeta";

// Display-time fact deduplication. The same SMS/email thread is often
// re-exported into a case multiple times (Plaud/SMS exports span overlapping
// dates), so identical facts surface from several records. We collapse them
// to ONE representative and list the other source records as corroboration
// provenance ("also stated in N records · <dates>"). This is purely a render
// concern — stored facts and the per-record detail view are untouched.
//
// Representative election (Round 11, Option B/C): the canonical instance sits
// at the fact's OWN event date (its chronology date, NOT the source record's
// upload date), tie-broken to the earliest source record by record_date. We
// deliberately do NOT attribute to a "richest"/fuller export: the live
// multi-export threads have ZERO proven containment (independent overlapping
// exports, not append-only supersets), so any fuller-source guess would be an
// unjustified heuristic. Shared facts are treated as independent corroboration
// attributed to when they happened.

export interface DedupedFact {
  // The representative fact: earliest event date, then earliest source record.
  fact: RecordFact;
  // record_ids of the OTHER records carrying the same fact (deduped,
  // excludes the representative's own record). Empty when unique.
  alsoIn: string[];
}

// Normalize text for duplicate comparison: trim, collapse internal
// whitespace, lowercase. Forwarded copies differ only in surrounding
// whitespace/case, so this catches them without over-merging distinct facts.
export function normalizeForDedup(s: string | null | undefined): string {
  return (s ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

// Stronger normalization for verbatim-quote dedup: lowercase, strip
// punctuation (periods, commas, quotes, colons, etc.), collapse whitespace,
// trim. Catches quotes that differ only in trailing punctuation or case
// ("…$2000 deductible" vs "…$2000 deductible.") without merging quotes whose
// WORDS differ — currency/letters/digits are preserved.
export function normalizeQuote(s: string | null | undefined): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/[.,:;!?'"“”‘’`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Dedup key for money facts (financial summary, coverage terms, valuations):
// the normalized numeric amount + first 50 chars of the normalized
// description. "$35" and "$35.00" collapse to one entry, while genuinely
// different descriptions at the same amount stay separate.
export function amountDedupKey(f: RecordFact): string {
  return `${parseAmount(f.amount)}|${normalizeForDedup(f.summary).slice(0, 50)}`;
}

// Is `a` the more canonical representative than `b`? Ordering, in priority:
//   1. earliest event date (the fact's OWN date — chronology, not the source
//      record's upload date). An empty date sorts last so a dated copy wins.
//   2. earliest source record by record_date (deterministic tie-break).
//   3. full tie → keep current (stable, input order).
//
// Round 11 (Option B/C): there is intentionally NO containment/"richest
// export" step. We measured the live multi-export SMS threads and found ZERO
// proven containment (no record's normalized body is a substring of another;
// even loose shingle overlap is 5–17%) — they are independent overlapping
// exports, not append-only supersets. A "richest by fact count" tie-break was
// an unjustified heuristic and has been removed: when two records genuinely
// share a fact, neither is the proven-fuller source, so we treat them as
// independent corroboration and attribute to the earliest event date. (A true
// append-only superset would still land here on earliest event date — that's
// the deliberate, deterministic fallback; see dedupeFacts.test.ts for the
// latent superset-shaped fixture that locks this in.)
function moreCanonical(
  a: RecordFact,
  b: RecordFact,
  recordById: Map<string, CaseRecord>,
): boolean {
  const da = a.date ?? "";
  const db = b.date ?? "";
  if (da !== db) {
    if (!da) return false;
    if (!db) return true;
    return da < db;
  }
  const ca = recordById.get(a.record_id)?.record_date ?? "";
  const cb = recordById.get(b.record_id)?.record_date ?? "";
  if (ca !== cb) {
    if (!ca) return false;
    if (!cb) return true;
    return ca < cb;
  }
  return false;
}

function electRepresentative(
  group: RecordFact[],
  recordById: Map<string, CaseRecord>,
): RecordFact {
  return group.reduce(
    (best, f) => (moreCanonical(f, best, recordById) ? f : best),
    group[0]!,
  );
}

// Collapse facts that share a key into one representative each. The default
// key is the normalized verbatim quote (an exact source substring, so
// forwarded copies match). Pass a custom keyOf to dedupe on other criteria
// (e.g. amount+description for the financial summary).
export function dedupeFacts(
  facts: RecordFact[],
  recordById: Map<string, CaseRecord>,
  keyOf: (f: RecordFact) => string = (f) => normalizeQuote(f.verbatim_quote),
): DedupedFact[] {
  const groups = new Map<string, RecordFact[]>();
  const order: string[] = [];
  for (const f of facts) {
    const k = keyOf(f);
    // A blank key can't be compared — keep the fact unique by its id.
    const key = k ? `k:${k}` : `id:${f.id}`;
    let g = groups.get(key);
    if (!g) {
      groups.set(key, (g = []));
      order.push(key);
    }
    g.push(f);
  }
  return order.map((key) => {
    const g = groups.get(key)!;
    const rep = electRepresentative(g, recordById);
    const alsoIn = [...new Set(g.map((f) => f.record_id))].filter(
      (id) => id !== rep.record_id,
    );
    return { fact: rep, alsoIn };
  });
}

// ---------------------------------------------------------------------------
// Category-section rollup (Round 9, Part 3). The Coverage section showed the
// same policy term restated per record ("$2,000 deductible" × 6 entries):
// quote-/amount-key dedup only collapses near-identical DESCRIPTIONS, while
// extraction words the same term differently per record ("Claimant has $2000
// deductible" / "Non-collision deductible amount" / "Comprehensive coverage
// deductible is $2000"). Within ONE category section, entries with the
// IDENTICAL signed verified amount (or no amount at all) whose normalized
// titles share content tokens roll up into one entry "stated in N records".
// DISPLAY-ONLY — stored facts are never merged; every rolled entry keeps all
// source quotes accessible. Different amounts NEVER roll up.
// ---------------------------------------------------------------------------

export interface RolledFact {
  // Representative entry (earliest source record, same rule as dedupeFacts).
  fact: RecordFact;
  alsoIn: string[];
  // The OTHER section entries rolled into this one; quotes stay reachable.
  corroborations: DedupedFact[];
  // Distinct source records across the whole rollup (incl. alsoIn).
  recordCount: number;
  // Distinct attributed parties across the rollup (party differences don't
  // block rollup, but every attributed party is listed).
  parties: string[];
}

// Generic words that can't carry a title match on their own — function words
// plus domain-generic nouns that appear in most facts of a section.
const TITLE_GENERIC = new Set([
  "the", "a", "an", "of", "for", "to", "in", "on", "at", "is", "are", "was",
  "were", "has", "have", "had", "be", "been", "being", "and", "or", "with",
  "under", "per", "may", "not", "no",
  "amount", "amounts", "coverage", "policy", "insurance", "claim", "claims",
  "claimant", "applied", "applies", "apply", "stated", "listed", "used",
  "using",
]);

// Content tokens of a fact title: currency and bare figures stripped (the
// amount is compared separately and exactly), then normalized words >= 3
// chars that aren't generic.
function titleTokens(summary: string): Set<string> {
  const stripped = summary
    .replace(/\$\s?(?:\d[\d,]*(?:\.\d+)?|\.\d+)/g, " ")
    .replace(/\b\d[\d,]*(?:\.\d+)?\b/g, " ");
  return new Set(
    normalizeForDedup(stripped)
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length >= 3 && !TITLE_GENERIC.has(w)),
  );
}

function sharedCount(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const t of a) if (b.has(t)) n++;
  return n;
}

// Summary label for a rolled-up entry's sources line. "Stated in 1 record"
// must never appear (Round 10, 1C): a single-record rollup describes
// same-record restatements, and says so.
export function rollupSourcesLabel(
  recordCount: number,
  corroborationCount: number,
): string {
  return recordCount > 1
    ? `Stated in ${recordCount} records`
    : `Stated ${corroborationCount + 1}× in this record`;
}

export function rollupCategoryFacts(
  deduped: DedupedFact[],
  recordById: Map<string, CaseRecord>,
): RolledFact[] {
  interface Group {
    members: DedupedFact[];
    tokens: Set<string>[];
    amountKey: string;
  }
  // Signed amount is the hard gate: identical parsed amount (or both
  // amount-less). An amount-bearing match needs 1 shared content token (the
  // amount itself is strong evidence); amount-less facts need 2 so unrelated
  // statements don't merge on a single common word. Transitive: joining any
  // member joins the group.
  const amountKeyOf = (f: RecordFact) =>
    f.amount?.trim() ? `amt:${parseAmount(f.amount)}` : "none";
  const groups: Group[] = [];
  for (const d of deduped) {
    const tok = titleTokens(d.fact.summary);
    const aKey = amountKeyOf(d.fact);
    const min = aKey === "none" ? 2 : 1;
    const g =
      tok.size > 0
        ? groups.find(
            (x) =>
              x.amountKey === aKey &&
              x.tokens.some((t) => sharedCount(t, tok) >= min),
          )
        : undefined;
    if (g) {
      g.members.push(d);
      g.tokens.push(tok);
    } else {
      groups.push({ members: [d], tokens: [tok], amountKey: aKey });
    }
  }
  return groups.map((g) => {
    // Same election as dedupeFacts: earliest event date, then earliest source
    // record. No "richest export" tie-break (Option B/C — see moreCanonical).
    const rep = g.members.reduce(
      (best, m) => (moreCanonical(m.fact, best.fact, recordById) ? m : best),
      g.members[0]!,
    );
    const recordIds = new Set<string>();
    const parties = new Set<string>();
    for (const m of g.members) {
      recordIds.add(m.fact.record_id);
      for (const id of m.alsoIn) recordIds.add(id);
      if (m.fact.party) parties.add(m.fact.party);
    }
    return {
      fact: rep.fact,
      alsoIn: rep.alsoIn,
      corroborations: g.members.filter((m) => m !== rep),
      recordCount: recordIds.size,
      parties: [...parties],
    };
  });
}
